/**
 * Default Mode Configuration
 * Includes all features and navigation items
 */

'use client';

import { ModeConfig } from './config';
import {
  Birdhouse,
  CreditCard,
  Wrench,
  Library,
  Gauge,
  TextInitial,
  Languages,
  Settings,
  Video,
} from 'lucide-react';
import {
  FileText,
  Bot,
  MessageCircle,
  ClipboardCheck,
  Menu,
  Clock,
  Users,
  Hash,
} from 'lucide-react';
import { TranscribeIcon } from './icons';
import { SageNavIcon } from '@/components/support/sage-nav-icon';

export const defaultMode: ModeConfig = {
  id: 'default',
  name: 'Default',
  // Information architecture (DESIGN.md, 5 Sep audit): four primary places,
  // two tools and distinct usage/billing account destinations.
  sidebarNav: [
    {
      name: 'Home',
      href: '/dashboard',
      icon: Birdhouse,
      messageKey: 'app.nav.home',
      section: 'workspace',
    },
    {
      name: 'Transcribe',
      href: '/dashboard/transcribe',
      icon: TranscribeIcon,
      messageKey: 'app.nav.transcribe',
      section: 'workspace',
    },
    {
      name: 'Library',
      href: '/dashboard/transcriptions',
      icon: Library,
      messageKey: 'app.nav.library',
      section: 'workspace',
    },
    {
      name: 'Meetings',
      href: '/dashboard/meetings',
      icon: Video,
      messageKey: 'app.nav.meetings',
      section: 'workspace',
    },
    {
      name: 'Translate',
      href: '/dashboard/translate',
      icon: Languages,
      messageKey: 'app.nav.translate',
      section: 'tools',
    },
    {
      name: 'Text to speech',
      href: '/dashboard/text-to-speech',
      icon: TextInitial,
      messageKey: 'app.nav.textToSpeech',
      section: 'tools',
    },
    {
      name: 'Free tools',
      href: '/dashboard/tools',
      icon: Wrench,
      section: 'tools',
    },
    {
      name: 'Usage',
      href: '/dashboard/usage',
      icon: Gauge,
      messageKey: 'app.nav.usage',
      section: 'account',
    },
    {
      name: 'Billing',
      href: '/dashboard/settings/billing',
      icon: CreditCard,
      messageKey: 'app.nav.billing',
      section: 'account',
    },
    {
      name: 'Settings',
      href: '/dashboard/settings',
      icon: Settings,
      messageKey: 'app.nav.settings',
      section: 'account',
    },
    {
      name: 'Support',
      href: '/dashboard/support',
      icon: SageNavIcon,
      messageKey: 'app.nav.support',
      section: 'account',
    },
  ],
  transcriptTabs: [
    { id: 'transcript', label: 'Transcript', icon: FileText, messageKey: 'app.tabs.transcript' },
    { id: 'summary', label: 'AI Summary', icon: Bot, messageKey: 'app.tabs.summary' },
    { id: 'chat', label: 'Wisprs Chat', icon: MessageCircle, messageKey: 'app.tabs.chat' },
    { id: 'actions', label: 'Action Points', icon: ClipboardCheck, messageKey: 'app.tabs.actions' },
    { id: 'chapters', label: 'Chapters', icon: Menu, messageKey: 'app.tabs.chapters' },
    { id: 'minutes', label: 'Minutes', icon: Clock, messageKey: 'app.tabs.minutes' },
    { id: 'speakers', label: 'Speaker Summary', icon: Users, messageKey: 'app.tabs.speakers' },
    { id: 'topics', label: 'Topic Summary', icon: Hash, messageKey: 'app.tabs.topics' },
  ],
  landingPage: {
    hero: {
      title: 'Turn speech into action',
      description: 'Fast, premium transcription + outputs that save time',
      cta: {
        primary: 'Start transcribing for free',
        secondary: 'View pricing',
      },
    },
    features: [
      {
        title: 'High-accuracy transcription',
        description: 'Powered by top-tier STT providers',
      },
      {
        title: 'AI-Powered Summaries',
        description: 'Get instant insights from your audio',
      },
      {
        title: 'Multi-Language Support',
        description: 'Transcribe in 134+ languages',
      },
    ],
  },
  routes: {
    dashboard: '/dashboard',
    upload: '/dashboard/upload',
    transcriptions: '/dashboard/transcriptions',
    success: '/dashboard?checkout=success',
  },
};
